import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibCheckbox } from '@chewy/kib-choices-react'
import { KibInfoOutlineIcon } from '@chewy/kib-icons-react'
import { KibContainer } from '@chewy/kib-layout-react'
import { KibTooltip, KibTooltipTrigger } from '@chewy/kib-tooltip-react'
import { NumberUtils } from '@pbt/pbt-ui-components'

import type { Payment } from '~/api/graphql/generated/types'

import {
  autoSelectPayments,
  createInvoiceIdMap,
  createInvoiceNumberMap,
  handleSelectPayment as handleSelectPaymentUtil,
  isPaymentDisabled,
  isPaymentSelected,
  type PaymentSelectionState,
} from '../utils/clientAccountCreditUtils'

import '@chewy/kib-tooltip-styles/dist/kib-tooltip.css'
import '@chewy/kib-choices-styles/dist/styles/kib-checkbox.css'
import styles from './ClientAccountCredit.module.scss'

interface ClientAccountCreditProps {
  businessId: string
  onAccountCreditChange?: (data: {
    accountCreditTotal: number
    selectedAccountCreditIds: string[]
  }) => void
  remainingBalance: number
  setRemainingBalance: (remainingBalance: number) => void
  unappliedPayments: Payment[]
}

export const ClientAccountCredit = ({
  unappliedPayments,
  remainingBalance,
  setRemainingBalance,
  businessId,
  onAccountCreditChange,
}: ClientAccountCreditProps) => {
  const { t } = useTranslation(['Common', 'Dialogs', 'Payments'])

  const [selectedPayments, setSelectedPayments] = useState<Payment[]>([])
  const [totalCreditAmount, setTotalCreditAmount] = useState(0)
  const [partiallyAppliedPayments, setPartiallyAppliedPayments] = useState<{
    [paymentId: string]: number
  }>({})
  const [isInitialized, setIsInitialized] = useState(false)

  const currentState: PaymentSelectionState = {
    selectedPayments,
    totalCreditAmount,
    remainingBalance,
    partiallyAppliedPayments,
  }

  const handleAutoSelectPayments = () => {
    const result = autoSelectPayments(unappliedPayments, remainingBalance)

    setSelectedPayments(result.selected)
    setTotalCreditAmount(result.currentTotalCredit)
    setRemainingBalance(result.workingRemainingBalance)
    setPartiallyAppliedPayments(result.partiallyApplied)
    if (onAccountCreditChange) {
      onAccountCreditChange({
        accountCreditTotal: result.currentTotalCredit,
        selectedAccountCreditIds: result.selected.map((payment) => payment.id),
      })
    }
  }

  useEffect(() => {
    if (!isInitialized && unappliedPayments.length > 0) {
      handleAutoSelectPayments()
      setIsInitialized(true)
    }
  }, [unappliedPayments, remainingBalance, isInitialized])

  const handleSelectPayment = (payment: Payment) => {
    const result = handleSelectPaymentUtil(payment, currentState)

    if (
      'newSelectedPayments' in result &&
      'balance' in result &&
      'total' in result
    ) {
      setSelectedPayments(result.newSelectedPayments)
      setRemainingBalance(result.balance)
      setTotalCreditAmount(result.total)
      setPartiallyAppliedPayments(result.newPartiallyApplied)
      if (onAccountCreditChange) {
        onAccountCreditChange({
          accountCreditTotal: result.total,
          selectedAccountCreditIds: result.newSelectedPayments.map(
            (paymentList) => paymentList.id,
          ),
        })
      }
    } else if (
      'newSelectedPayments' in result &&
      'newTotalCreditAmount' in result
    ) {
      setSelectedPayments(result.newSelectedPayments)
      setTotalCreditAmount(result.newTotalCreditAmount)
      setRemainingBalance(result.newRemainingBalance)
      setPartiallyAppliedPayments(result.newPartiallyAppliedPayments)
      if (onAccountCreditChange) {
        onAccountCreditChange({
          accountCreditTotal: result.newTotalCreditAmount,
          selectedAccountCreditIds: result.newSelectedPayments.map(
            (paymentList) => paymentList.id,
          ),
        })
      }
    }
  }
  const infoIconRefs = useMemo(() => {
    const refs: { [key: string]: React.RefObject<HTMLButtonElement> } = {}
    unappliedPayments.forEach((payment) => {
      refs[payment.id] = React.createRef<HTMLButtonElement>()
    })
    return refs
  }, [unappliedPayments])

  const invoiceNumberMap = useMemo(
    () =>
      createInvoiceNumberMap(
        unappliedPayments,
        t('Dialogs:ADD_PAYMENT_DIALOG_V2.NO_INVOICE_NUMBER'),
      ),
    [unappliedPayments, t],
  )

  const invoiceIdMap = useMemo(
    () =>
      createInvoiceIdMap(
        unappliedPayments,
        t('Dialogs:ADD_PAYMENT_DIALOG_V2.NO_INVOICE_ID'),
      ),
    [unappliedPayments, t],
  )

  return (
    <div className={styles.clientAccountCreditGridItem}>
      <KibContainer className={styles.clientAccountCreditContainer}>
        <div className={styles.headerSection}>
          <h4 className={styles.sectionTitle}>
            {t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.APPLY_CLIENT_ACCOUNT_CREDIT_TITLE',
            )}
          </h4>
          <div className={styles.subtitle}>
            {t(
              'Dialogs:ADD_PAYMENT_DIALOG_V2.APPLY_CLIENT_ACCOUNT_CREDIT_SUBTITLE',
            )}
          </div>
        </div>
        <div className={styles.creditOptions}>
          {unappliedPayments.map((payment) => (
            <div className={styles.creditOption} key={payment.id}>
              <div className={styles.checkboxWithLabel}>
                <div className={styles.checkboxAndIcon}>
                  <KibCheckbox
                    checked={isPaymentSelected(payment, selectedPayments)}
                    className={styles.checkbox}
                    disabled={isPaymentDisabled(
                      payment,
                      selectedPayments,
                      remainingBalance,
                    )}
                    label={`${NumberUtils.formatMoney(payment.unappliedAmount)}`}
                    onChange={() => handleSelectPayment(payment)}
                  />

                  <div className={styles.infoIconContainer}>
                    <KibTooltipTrigger
                      className={styles.infoIconTrigger}
                      ref={infoIconRefs[payment.id]}
                    >
                      <KibInfoOutlineIcon className={styles.infoIcon} />
                    </KibTooltipTrigger>
                    <KibTooltip
                      placement="top"
                      popperModifiers={[]}
                      trigger={infoIconRefs[payment.id]}
                    >
                      <div className={styles.tooltipContent}>
                        <div className={styles.tooltipTitle}>
                          {payment.type?.name === 'Payment'
                            ? t(
                                'Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_APPLIED_TOOLTIP_TITLE_PAYMENT',
                              )
                            : t('Payments:PAYMENT_TYPES.DEPOSIT')}
                        </div>
                        <div className={styles.tooltipInvoiceNumberContainer}>
                          <span className={styles.tooltipInvoiceNumber}>
                            {`${t('Common:INVOICE')}:`}
                          </span>
                          {invoiceIdMap.get(payment.id) !==
                          t('Dialogs:ADD_PAYMENT_DIALOG_V2.NO_INVOICE_ID') ? (
                            <a
                              className={styles.tooltipInvoiceNumberLink}
                              href={`/invoice/${invoiceIdMap.get(payment.id)}?businessId=${businessId}`}
                              rel="noopener noreferrer"
                              target="_blank"
                            >
                              {`${invoiceNumberMap.get(payment.id)}`}
                            </a>
                          ) : (
                            <span className={styles.tooltipInvoiceNumberLink}>
                              {`${invoiceNumberMap.get(payment.id)}`}
                            </span>
                          )}
                        </div>
                      </div>
                    </KibTooltip>
                  </div>
                </div>
              </div>

              <div className={styles.remainingCreditRow}>
                {Boolean(partiallyAppliedPayments[payment.id]) && (
                  <div className={styles.remainingCreditAmount}>
                    {`${NumberUtils.formatMoney(partiallyAppliedPayments[payment.id])} ${t('Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_APPLIED_REMAINING_SUBTITLE')}`}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className={styles.summarySection}>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>
              {t('Dialogs:ADD_PAYMENT_DIALOG_V2.CREDIT_APPLIED')}
            </span>
            <span className={styles.summaryValue}>
              ({NumberUtils.formatMoney(totalCreditAmount)})
            </span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>
              {t('Dialogs:ADD_PAYMENT_DIALOG_V2.REMAINING_BALANCE')}
            </span>
            <span className={styles.summaryValue}>
              {NumberUtils.formatMoney(remainingBalance)}
            </span>
          </div>
        </div>
      </KibContainer>
    </div>
  )
}

export default ClientAccountCredit
